import React from "react";
import {
  MessageSquare, Phone, Mail, FileText, Activity, Search,
  Sparkles, Stethoscope, Users,
} from "lucide-react";
import RichBlogLayout from "../../components/RichBlogLayout";
import {
  Prose, Lead, PullQuote, MetricsRow, BeforeAfter, ProcessFlow,
  ParallelGrid, FeatureCallout,
  TimelineList, SectionDivider,
} from "../../components/blog/BlogGraphics";

const BlogUnifiedPatientTimeline = () => (
  <RichBlogLayout
    category="Patient Communication"
    eyebrow="Patient Communication"
    title="One Timeline, Every Conversation: How a Unified Patient Story Replaces Your Comms Stack"
    subtitle="SMS, email, voice — interleaved by timestamp, sentiment-tagged, and attached to the chart so the next teammate never starts from zero."
    author="Asha Team"
    date="May 2, 2026"
    readTime="8 min read"
  >
    <Lead>
      It's 2:14 PM on Wednesday. James calls back and opens with, "Hey, I got a
      text on Tuesday about my balance, and last Thursday somebody from your
      office called me about insurance — what's the latest?" The teammate on
      the line is now hunting through three systems while James waits. That
      30-second silence is where care quality and revenue both quietly leak.
    </Lead>

    <Prose>
      <p>
        Walk through the average dental practice's communication stack and
        count the systems. Texts in one platform. Email in a marketing tool.
        Phone calls logged — if they're logged at all — in a VOIP dashboard
        nobody opens. Clinical conversations buried in chart notes. Billing
        outreach in a separate billing UI. Each tool perfectly tracks its
        own slice. None of them know about the others.
      </p>
      <p>
        The patient's story is never actually in one place. It lives across
        tools, across channels, and most damagingly, across the memory of
        whichever teammate happened to be in the room at the time. When that
        teammate is on PTO or has moved to another practice, the story
        disappears with them.
      </p>
    </Prose>

    <MetricsRow
      items={[
        { value: 1, label: "Unified timeline per patient" },
        { value: 4, label: "Channels interleaved by timestamp" },
        { value: 0, label: "Tabs to switch between" },
        { value: 100, suffix: "%", label: "Of comms tied to the chart" },
      ]}
    />

    <Prose>
      <h2>Fragmented isn't a feature, it's a bill</h2>
      <p>
        Most practice management systems were built before SMS was a thing.
        Voice was a phone and a notepad. Texts were a "patient engagement
        add-on" you bolted on later. Email was its own marketing tool. The
        result is that the modern practice owns five different views of the
        same patient and no view of the patient as a whole.
      </p>
      <p>
        When a patient says "I told someone last week," the team has to
        believe them — there is no fast way to verify. When billing wants to
        know if a clinical conversation is in flight, they cannot tell. When
        the front desk wants to know whether a balance reminder went out
        before they pick up the phone, they have to ask.
      </p>
    </Prose>

    <BeforeAfter
      beforeTitle="The fragmented comms stack"
      afterTitle="One unified timeline"
      beforeItems={[
        "Texts in one platform, email in another, voice in a VOIP dashboard",
        "Clinical conversations buried in unstructured chart notes",
        "Each teammate sees only what their tool happens to track",
        "Patient context lives in whoever was in the room at the time",
      ]}
      afterItems={[
        "Every SMS, email, and voice call interleaved by timestamp",
        "AI call transcripts in line with the rest of the conversation",
        "Sentiment-tagged so urgency or frustration is immediately visible",
        "Attached to the chart — searchable, exportable, always available",
      ]}
    />

    <Prose>
      <h2>The shape of one continuous thread</h2>
      <p>
        The most useful way to see what a unified timeline does is to look at
        a real patient week. Take Maria Lopez. On Tuesday, Rachel sent a
        billing SMS about a $312.40 balance from her April 14 visit. On
        Thursday, Amy answered an inbound call to confirm her hygiene recall.
        The following Monday, the hygienist sent a clinical post-op text.
      </p>
      <p>
        In a fragmented stack, those three events live in three systems. In
        Asha, they are one scroll, in order, with the speaker, the channel,
        the sentiment, and the action items already attached.
      </p>
    </Prose>

    <TimelineList
      title="Patient timeline · Maria Lopez · this week"
      events={[
        { time: "Tue 9:14 AM", channel: "sms", label: "Billing follow-up sent · $312.40", by: "Rachel · tone-matched, casual" },
        { time: "Tue 11:02 AM", channel: "sms", label: "Patient replied · split-pay request", by: "Maria · positive sentiment" },
        { time: "Tue 11:03 AM", channel: "sms", label: "Payment plan offered · $156.20 today", by: "Rachel" },
        { time: "Tue 11:08 AM", channel: "form", label: "Payment received · ledger updated", by: "Stripe · auto-posted" },
        { time: "Thu 2:41 PM", channel: "call", label: "Inbound call · hygiene recall confirmation", by: "Amy · 4 min · neutral sentiment" },
        { time: "Thu 2:46 PM", channel: "sms", label: "Confirmation sent · Tue 9:30 AM hygiene", by: "Amy" },
        { time: "Mon 10:14 AM", channel: "sms", label: "Post-op check-in · D2740 crown #14", by: "Hygienist · clinical" },
        { time: "Mon 11:20 AM", channel: "sms", label: "Patient replied · 'feeling great, thanks!'", by: "Maria · positive sentiment" },
      ]}
    />

    <Prose>
      <h2>Calls become structured rows, not audio files</h2>
      <p>
        The hardest channel to unify has historically been voice. A phone call
        was an opaque event — it happened, maybe somebody wrote a sticky
        note, and the audio recording sat in a system nobody opened. In a
        unified timeline, the call is a row like any other, and clicking it
        opens a structured view that lives next to the SMS thread above it.
      </p>
    </Prose>

    <ProcessFlow
      steps={[
        {
          title: "Every call lands in line, by timestamp",
          body:
            "Inbound or outbound, AI-handled or human, the moment the call ends it inserts itself into the timeline in chronological order with the surrounding texts and emails.",
        },
        {
          title: "Speaker-diarized transcript, ready to read",
          body:
            "No more listening to a 7-minute recording. Each call card opens to a clean transcript with 'who said what' clearly labeled.",
        },
        {
          title: "Sentiment timeline shows tone shifts",
          body:
            "Frustration spikes, hesitation, agreement — surfaced as a sentiment line so the next teammate can see the emotional arc at a glance.",
        },
        {
          title: "Action items extracted and routed",
          body:
            "Follow-ups from the call become tasks on the right teammate's queue automatically, with the linked transcript and patient chart attached.",
        },
        {
          title: "Audio embedded for the rare deep dive",
          body:
            "When a transcript isn't enough, the audio player is right inside the call card — never a separate recording system to log into.",
        },
      ]}
    />

    <PullQuote>
      So when staff picks up mid-thread, they already know the full history. And when the patient calls back, the next person doesn't start from zero. They continue the story.
    </PullQuote>

    <Prose>
      <h2>What this unlocks for the next teammate</h2>
      <p>
        The most underappreciated feature of a unified timeline is what
        happens when staff turn over. A new front-desk hire can pick up an
        active conversation on day one because the conversation is the chart.
        A floating hygienist who hasn't seen this patient before can scan
        thirty seconds of timeline and walk into the room knowing the
        patient's last billing concern, last clinical question, and last
        sentiment.
      </p>
    </Prose>

    <ParallelGrid
      title="One timeline, every channel landing in the same place"
      columns={4}
      items={[
        { title: "SMS", subtitle: "Billing · clinical · scheduling", icon: MessageSquare, status: "active" },
        { title: "Voice", subtitle: "Inbound + outbound", icon: Phone, status: "active" },
        { title: "Email", subtitle: "Marketing + transactional", icon: Mail, status: "active" },
        { title: "Forms", subtitle: "Intake + consent", icon: FileText, status: "active" },
        { title: "AI transcripts", subtitle: "Diarized + sentiment", icon: Activity, status: "done" },
        { title: "Action items", subtitle: "Routed automatically", icon: Sparkles, status: "done" },
        { title: "Tied to chart", subtitle: "Searchable forever", icon: Search, status: "done" },
        { title: "Care continuity", subtitle: "Any teammate, any moment", icon: Users, status: "done" },
      ]}
    />

    <Prose>
      <h2>The three quiet wins</h2>
      <p>
        On the surface, a unified timeline looks like a UI improvement. In
        practice, three things change that aren't obvious until you've
        operated without them for a week.
      </p>
      <p>
        First, the patient stops repeating themselves. They reference Tuesday
        and the team can see Tuesday. Second, internal handoffs collapse —
        the clinical team can see the billing thread and the billing team
        can see the clinical thread without a meeting. Third, every action
        the practice takes is informed by what just happened. A "we miss you"
        email never accidentally fires the day after a billing dispute.
      </p>
    </Prose>

    <FeatureCallout
      stat="One scroll"
      label="From every channel to one chronological story"
      description="Every conversation Maria has ever had with the practice — texts, emails, calls, forms — interleaved in order, sentiment-tagged, attached to the chart."
      icon={Stethoscope}
    />

    <Prose>
      <h2>How the morning huddle changes</h2>
      <p>
        The morning huddle stops being a recap of who-said-what. The front
        desk stops asking the back office for context. The clinical team
        walks into rooms knowing exactly what was said outside the room.
        Patients stop being asked the same question twice — and stop
        explaining their own history to a person whose system doesn't show it.
      </p>
      <p>
        From the operator's seat, the deepest shift is institutional memory.
        When a teammate leaves, their relationships do not leave with them.
        When a patient comes back after a year away, the next person on the
        line knows exactly where the relationship paused.
      </p>
    </Prose>

    <SectionDivider label="Bottom line" />

    <Prose>
      <p>
        Communication that lives in five systems is communication that lives
        in nobody's head. A unified, chronological, sentiment-tagged timeline
        attached to the chart is the difference between a practice that
        remembers the patient and a practice where the patient has to
        remember for them.
      </p>
    </Prose>
  </RichBlogLayout>
);

export default BlogUnifiedPatientTimeline;
